import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PackageCheck,
  ArrowLeft,
  PhoneCall,
  ShoppingBag,
  RefreshCw,
  CheckCircle2,
  Store,
  ShoppingCart,
  Zap,
  ShieldCheck,
  Clock,
  Check,
  Link2,
  UserPlus,
  MessageCircle,
  Percent,
  FileSpreadsheet,
  Globe,
  ExternalLink,
  Layers,
  Headphones,
  TrendingUp,
} from 'lucide-react';
import CoreSellLogo from '../components/CoreSellLogo';
import LandingSeoHead from '../components/LandingSeoHead';
import { useAuth } from '../contexts/AuthContext';
import { landingSeo } from '../config/landingSeo';
import '../styles/landing.css';

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const goToPanel = () => {
    navigate(isAuthenticated ? '/dashboard' : '/auth/login');
  };

  return (
    <>
      <LandingSeoHead />
      <div className="landing-page antialiased selection:bg-brand-500 selection:text-white min-h-screen">
      {/* Background Elements */}
      <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute top-0 -right-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
      </div>

      {/* Navigation */}
      <header>
      <nav className="fixed w-full z-50 glass-panel border-b-0 border-white/5 transition-all duration-300" aria-label="منوی اصلی">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <CoreSellLogo to="/" />
            <div className="hidden lg:flex items-center gap-7 text-sm">
              <a href="#features" className="text-slate-300 hover:text-white transition">مزایا و امکانات</a>
              <a href="#pricing-sync" className="text-slate-300 hover:text-white transition">همگام‌سازی قیمت</a>
              <a href="#how-it-works" className="text-slate-300 hover:text-white transition">چگونه کار می‌کند؟</a>
              <a href="#faq" className="text-slate-300 hover:text-white transition">سوالات متداول</a>
              <a href="#contact" className="text-blue-400 hover:text-blue-300 font-medium transition flex items-center gap-1.5">
                <Globe className="w-4 h-4" />
                تماس و استودیو
              </a>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://altostudio.ir"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-300 bg-slate-800/70 hover:bg-slate-700/80 px-3 py-1.5 rounded-full border border-slate-700 transition"
                title="وب‌سایت آلتو استودیو"
              >
                <Globe className="w-3.5 h-3.5 text-blue-400" />
                <span>altostudio.ir</span>
              </a>
              <button
                type="button"
                onClick={goToPanel}
                className="hidden md:block text-slate-300 hover:text-white text-sm font-medium transition px-2"
              >
                ورود به پنل
              </button>
              <button
                type="button"
                onClick={goToPanel}
                className="bg-white text-slate-900 px-5 py-2.5 rounded-full text-sm font-bold hover:bg-slate-100 transition shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              >
                تست رایگان
              </button>
            </div>
          </div>
        </div>
      </nav>
      </header>

      <main>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden" aria-labelledby="hero-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-800/60 border border-slate-700 text-xs sm:text-sm text-blue-400 mb-8 animate-fade-in-up">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
            </span>
            <span>متصل به پنل جدید فروشندگان دیجی‌کالا، باسلام و ووکامرس · همگام‌سازی لحظه‌ای موجودی و قیمت</span>
          </div>

          <h1
            id="hero-heading"
            className="text-4xl sm:text-6xl md:text-7xl font-extrabold text-white tracking-tight mb-8 animate-fade-in-up !text-center"
            style={{ animationDelay: '0.1s', lineHeight: 1.25 }}
          >
            فروش همزمان در چند کانال <br />
            <span className="gradient-text">بدون ترس از ناموجودی و خطای قیمت!</span>
          </h1>

          <p
            className="mt-6 text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            دیگر نیازی نیست با هر فروش یا تغییر نرخ، موجودی و قیمت‌ها را در پنل‌های مختلف دستی کم و زیاد کنید. کورسل (CoreSell) موجودی انبار و قیمت‌گذاری درگاه‌ها (دیجی‌کالا، باسلام و سایت شخصی) را به‌صورت خودکار، درصدی و در لحظه یکسان می‌کند.
          </p>

          <div
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up"
            style={{ animationDelay: '0.3s' }}
          >
            <button
              type="button"
              onClick={goToPanel}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all hover:scale-105 flex items-center justify-center gap-2"
            >
              شروع استفاده رایگان
              <ArrowLeft className="w-5 h-5" />
            </button>
            <a
              href="https://altostudio.ir"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-panel text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-slate-800 transition-all flex items-center justify-center gap-2.5 group"
            >
              <Globe className="w-5 h-5 text-blue-400 group-hover:scale-110 transition-transform" />
              <span>تماس و مشاوره (altostudio.ir)</span>
              <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
            </a>
          </div>
        </div>

        {/* Dashboard Activity Mockup */}
        <div
          className="max-w-4xl mx-auto mt-16 px-4 sm:px-6 relative animate-fade-in-up"
          style={{ animationDelay: '0.5s' }}
        >
          <div className="glass-panel rounded-2xl p-2 border border-slate-700/50 shadow-2xl overflow-hidden relative">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-900/80 border-b border-slate-800">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <span className="text-xs text-slate-400 font-medium">گزارش زنده سیستم انبار و قیمت‌گذاری کورسل</span>
            </div>

            <div className="bg-slate-950 p-6 md:p-8 flex flex-col gap-4 relative">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-950 z-10 pointer-events-none" />

              <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
                  <ShoppingBag className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-white font-medium mb-1">
                    یک سفارش جدید در <span className="text-red-400 font-bold">دیجی‌کالا</span> ثبت شد!
                  </p>
                  <p className="text-sm text-slate-400">کالای فروخته شده: کت چرم مردانه (سایز L - رنگ مشکی)</p>
                </div>
                <span className="mr-auto text-xs text-slate-500 mt-1">همین الان</span>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-xl bg-blue-900/20 border border-blue-900/30 ml-4 sm:ml-8 relative before:absolute before:-right-4 before:top-1/2 before:-translate-y-1/2 before:w-4 before:h-px before:bg-blue-800">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                  <RefreshCw className="w-5 h-5 text-blue-400 animate-spin-slow" />
                </div>
                <div>
                  <p className="text-white font-medium mb-1">سیستم در حال همگام‌سازی لحظه‌ای موجودی و نرخ‌ها...</p>
                  <p className="text-sm text-slate-400">موجودی انبار مرکزی از ۸ به ۷ عدد کاهش یافت.</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-xl bg-green-900/20 border border-green-900/30 ml-4 sm:ml-8 relative before:absolute before:-right-4 before:top-1/2 before:-translate-y-1/2 before:w-4 before:h-px before:bg-green-800 z-20">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
                <div className="w-full">
                  <p className="text-green-400 font-medium mb-2">موجودی و قیمت هدف در تمام درگاه‌ها هماهنگ شد ✓</p>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-300">
                    <span className="bg-slate-800 px-2.5 py-1 rounded">سایت شخصی: ۷ عدد (۱,۸۰۰,۰۰۰ تومان)</span>
                    <span className="bg-slate-800 px-2.5 py-1 rounded">باسلام (+۱۰٪): ۷ عدد (۱,۹۸۰,۰۰۰ تومان)</span>
                    <span className="bg-slate-800 px-2.5 py-1 rounded">دیجی‌کالا (+۱۵٪): ۷ عدد (۲,۰۷۰,۰۰۰ تومان)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section id="integrations" className="py-10 border-y border-slate-800 bg-slate-900/30" aria-labelledby="integrations-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="integrations-heading" className="text-sm font-medium text-slate-500 mb-8 uppercase tracking-widest">
            پشتیبانی از پلتفرم‌ها و درگاه‌های اصلی فروش در ایران
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-85 hover:opacity-100 transition-all duration-500">
            <div className="flex items-center gap-3 font-bold text-xl md:text-2xl text-red-500">
              <div className="p-2.5 bg-red-500/10 border border-red-500/20 rounded-xl">
                <ShoppingBag className="w-7 h-7" />
              </div>
              دیجی‌کالا
            </div>
            <div className="flex items-center gap-3 font-bold text-xl md:text-2xl text-orange-500">
              <div className="p-2.5 bg-orange-500/10 border border-orange-500/20 rounded-xl">
                <Store className="w-7 h-7" />
              </div>
              باسلام
            </div>
            <div className="flex items-center gap-3 font-bold text-xl md:text-2xl text-indigo-400">
              <div className="p-2.5 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
                <Layers className="w-7 h-7" />
              </div>
              ووکامرس و وردپرس
            </div>
            <div className="flex items-center gap-3 font-bold text-xl md:text-2xl text-purple-400">
              <div className="p-2.5 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                <ShoppingCart className="w-7 h-7" />
              </div>
              سایت شخصی و API
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 relative" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 id="features-heading" className="text-3xl md:text-5xl font-bold mb-6">پایان کابوس مدیریت موجودی و قیمت</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              تمام فرآیندهای تکراری انبارداری و قیمت‌گذاری را به هوش و صف‌های کورسل بسپارید تا با خیال راحت روی فروش و تامین تمرکز کنید.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="glass-panel p-8 rounded-2xl hover:-translate-y-2 transition-transform duration-300 group border-t-4 border-t-blue-500">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-500/30 transition">
                <Zap className="w-7 h-7 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">هماهنگی در لحظه (ثانیه‌ای)</h3>
              <p className="text-slate-400 leading-relaxed text-sm md:text-base">
                به محض اینکه مشتری در دیجی‌کالا یا سایت خرید کند، کورسل در کمتر از یک ثانیه موجودی کالا را در تمام پلتفرم‌ها کسر می‌کند تا کالای ناموجود فروخته نشود.
              </p>
            </div>

            <div className="glass-panel p-8 rounded-2xl hover:-translate-y-2 transition-transform duration-300 group border-t-4 border-t-purple-500">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-purple-500/30 transition">
                <Percent className="w-7 h-7 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">قیمت‌گذاری خودکار و درصدی</h3>
              <p className="text-slate-400 leading-relaxed text-sm md:text-base">
                برای هر درگاه درصد کارمزد اختصاصی تعیین کنید (مثلاً +۱۵٪ دیجی‌کالا). با تغییر قیمت پایه در انبار مرکزی، قیمت همه پلتفرم‌ها خودکار محاسبه و اعمال می‌شود.
              </p>
            </div>

            <div className="glass-panel p-8 rounded-2xl hover:-translate-y-2 transition-transform duration-300 group border-t-4 border-t-emerald-500">
              <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-500/30 transition">
                <FileSpreadsheet className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">ورود اکسل و انبارداری سریع</h3>
              <p className="text-slate-400 leading-relaxed text-sm md:text-base">
                برای انبارگردانی سریع، کافی است فایل اکسل موجودی انبار را بارگذاری کنید تا موجودی هزاران کالا و تنوع همزمان در تمام کانال‌ها به‌روزرسانی شود.
              </p>
            </div>

            <div className="glass-panel p-8 rounded-2xl hover:-translate-y-2 transition-transform duration-300 group border-t-4 border-t-red-500">
              <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-500/30 transition">
                <ShieldCheck className="w-7 h-7 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">خداحافظی با جریمه لغو سفارش</h3>
              <p className="text-slate-400 leading-relaxed text-sm md:text-base">
                بزرگ‌ترین زیان آنلاین‌شاپ‌ها جریمه لغو سفارش به‌دلیل اتمام موجودی است. با کورسل خطای انسانی حذف شده و نمره عملکرد فروشندگی شما همیشه در بالاترین حد می‌ماند.
              </p>
            </div>

            <div className="glass-panel p-8 rounded-2xl hover:-translate-y-2 transition-transform duration-300 group border-t-4 border-t-indigo-500">
              <div className="w-14 h-14 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-indigo-500/30 transition">
                <Layers className="w-7 h-7 text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">افزونه اختصاصی وردپرس و ووکامرس</h3>
              <p className="text-slate-400 leading-relaxed text-sm md:text-base">
                فروشگاه وردپرسی خود را به کمک افزونه و وب‌هوک اختصاصی به انبار متصل کنید؛ دریافت سفارش، کاهش موجودی و بروزرسانی قیمت کاملاً خودکار و دوطرفه انجام می‌شود.
              </p>
            </div>

            <div className="glass-panel p-8 rounded-2xl hover:-translate-y-2 transition-transform duration-300 group border-t-4 border-t-amber-500">
              <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-500/30 transition">
                <Clock className="w-7 h-7 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">صرفه‌جویی در ساعت‌ها زمان</h3>
              <p className="text-slate-400 leading-relaxed text-sm md:text-base">
                دیگر لازم نیست ساعات طولانی از روز را صرف باز کردن پنل‌های مختلف و ویرایش دستی قیمت‌ها و اعداد موجودی کنید. سیستم همه‌چیز را بی‌نقص مدیریت می‌کند.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Central Warehouse & Pricing Sync Showcase */}
      <section id="pricing-sync" className="py-20 relative bg-slate-900/30 border-y border-slate-800" aria-labelledby="pricing-sync-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-panel p-8 md:p-12 rounded-3xl overflow-hidden relative border border-slate-700/50">
            <div className="absolute left-0 top-0 w-80 h-80 bg-blue-500/10 blur-3xl rounded-full pointer-events-none" />
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-xs font-semibold text-blue-400 mb-4">
                  <TrendingUp className="w-4 h-4" />
                  مدیریت هوشمند قیمت و انبار
                </div>
                <h3 id="pricing-sync-heading" className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  یک انبار مرکزی با کنترل قیمت هوشمند برای همه فروشگاه‌ها
                </h3>
                <p className="text-slate-400 leading-relaxed text-base sm:text-lg mb-6">
                  موجودی و قیمت پایه محصول را در کورسل مشخص کنید. سیستم با توجه به درصد کارمزد دیجی‌کالا و باسلام، قیمت نهایی را برای هر کانال محاسبه و ظرف چند ثانیه اعمال می‌کند.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2.5 text-slate-300 text-sm">
                    <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                    <span>پشتیبانی کامل از تنوع رنگ، سایز و گارانتی کالا</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-slate-300 text-sm">
                    <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                    <span>امکان غیرفعال‌سازی موقت قیمت یا موجودی یک درگاه</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-slate-300 text-sm">
                    <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                    <span>ورود و خروج دسته‌ای داده‌ها از طریق فایل اکسل</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-slate-300 text-sm">
                    <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                    <span>صف‌های ارسال امن و بدون افت سرعت به دیجی‌کالا</span>
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-5/12">
                <div className="bg-slate-900 rounded-2xl border border-slate-700/80 p-5 shadow-2xl">
                  <div className="flex justify-between items-center mb-4 pb-3 border-b border-slate-800">
                    <div>
                      <span className="font-bold text-white text-sm">پنل هوشمند انبار کورسل</span>
                      <p className="text-[11px] text-slate-400">نمای محاسبه خودکار درصدها</p>
                    </div>
                    <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2.5 py-1 rounded-full border border-emerald-500/30">
                      همگام آنلاین
                    </span>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-slate-950/70 p-3.5 rounded-xl border border-slate-800">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white text-sm font-medium">ساعت هوشمند اولترا</span>
                        <span className="text-xs text-blue-400 font-bold">قیمت پایه: ۱,۲۰۰,۰۰۰ تومان</span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-slate-400">
                        <span>موجودی انبار مرکزی:</span>
                        <span className="text-white font-bold bg-slate-800 px-2 py-0.5 rounded">۱۵ عدد</span>
                      </div>
                    </div>

                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between items-center p-2.5 rounded-lg bg-red-950/20 border border-red-900/30 text-slate-300">
                        <span className="flex items-center gap-1.5 text-red-400 font-medium">
                          <Link2 className="w-3.5 h-3.5" />
                          دیجی‌کالا (+۱۵٪ کمیسیون)
                        </span>
                        <span className="font-bold text-white">۱,۳۸۰,۰۰۰ تومان (۱۵ عدد)</span>
                      </div>

                      <div className="flex justify-between items-center p-2.5 rounded-lg bg-orange-950/20 border border-orange-900/30 text-slate-300">
                        <span className="flex items-center gap-1.5 text-orange-400 font-medium">
                          <Link2 className="w-3.5 h-3.5" />
                          باسلام (+۸٪ کمیسیون)
                        </span>
                        <span className="font-bold text-white">۱,۲۹۶,۰۰۰ تومان (۱۵ عدد)</span>
                      </div>

                      <div className="flex justify-between items-center p-2.5 rounded-lg bg-indigo-950/20 border border-indigo-900/30 text-slate-300">
                        <span className="flex items-center gap-1.5 text-indigo-400 font-medium">
                          <Link2 className="w-3.5 h-3.5" />
                          ووکامرس / سایت شخصی
                        </span>
                        <span className="font-bold text-white">۱,۲۰۰,۰۰۰ تومان (۱۵ عدد)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-24 bg-slate-900/20 border-b border-slate-800" aria-labelledby="how-it-works-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="how-it-works-heading" className="text-3xl md:text-5xl font-bold mb-6">چگونه کورسل کار می‌کند؟</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              در چهار گام ساده، تمام کانال‌های فروشگاهی خود را متصل کنید و فروش چند کاناله را بدون استرس مدیریت فرمایید.
            </p>
          </div>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 list-none p-0 m-0">
            {landingSeo.howItWorks.map((item) => (
              <li key={item.step} className="glass-panel p-6 rounded-2xl relative">
                <span className="absolute -top-3 right-6 w-8 h-8 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center text-sm shadow-lg shadow-blue-500/30" aria-hidden="true">
                  {item.step}
                </span>
                <h3 className="text-lg font-bold text-white mb-3 mt-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Contact & Alto Studio Section */}
      <section id="contact" className="py-24 relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900/60 to-slate-950" aria-labelledby="contact-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-xs font-semibold text-blue-400 mb-4">
              <Globe className="w-4 h-4" />
              ارتباط مستقیم با تیم سازنده
            </div>
            <h2 id="contact-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              مشاوره، راه‌اندازی و پشتیبانی اختصاصی
            </h2>
            <p className="text-slate-400 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
              سامانه کورسل توسط تیم حرفه‌ای <span className="text-white font-bold">آلتو استودیو</span> طراحی، پیاده‌سازی و پشتیبانی می‌شود. برای دریافت مشاوره متناسب با آنلاین‌شاپ خود یا درخواست سفارشی‌سازی با ما در تماس باشید.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1: Alto Studio Website */}
            <div className="glass-panel p-8 rounded-2xl border-t-4 border-t-blue-500 hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center mb-6">
                  <Globe className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">وب‌سایت رسمی آلتو استودیو</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  برای آشنایی با سایر محصولات، خدمات توسعه نرم‌افزار، مقالات تخصصی و پورتفولیو به سایت رسمی ما مراجعه کنید.
                </p>
                <div className="bg-slate-900/80 p-3.5 rounded-xl border border-slate-800 text-sm text-center mb-6 font-mono text-blue-300" dir="ltr">
                  altostudio.ir
                </div>
              </div>
              <a
                href="https://altostudio.ir"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white py-3 px-5 rounded-xl font-bold text-sm transition shadow-lg shadow-blue-600/30"
              >
                <span>مشاهده altostudio.ir</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Card 2: Consultation & Onboarding */}
            <div className="glass-panel p-8 rounded-2xl border-t-4 border-t-purple-500 hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 bg-purple-500/20 rounded-2xl flex items-center justify-center mb-6">
                  <PhoneCall className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">مشاوره تخصصی آنلاین‌شاپ</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  بررسی تعداد محصولات، کانال‌های فروش فعال شما (دیجی‌کالا، باسلام، ووکامرس) و ارائه بهترین نقشه راه برای اتصال انبار مرکزی.
                </p>
                <div className="bg-slate-900/80 p-3.5 rounded-xl border border-slate-800 text-sm text-center mb-6 text-slate-300">
                  همراهی در تمام مراحل نصب و راه‌اندازی
                </div>
              </div>
              <a
                href="https://altostudio.ir"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 glass-panel hover:bg-slate-800 text-white py-3 px-5 rounded-xl font-bold text-sm transition"
              >
                <Globe className="w-4 h-4 text-purple-400" />
                <span>ثبت درخواست مشاوره در استودیو</span>
              </a>
            </div>

            {/* Card 3: Technical Support & Customization */}
            <div className="glass-panel p-8 rounded-2xl border-t-4 border-t-emerald-500 hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center mb-6">
                  <Headphones className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">پشتیبانی فنی و سفارشی‌سازی</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  نیاز به اتصال به سیستم‌های حسابداری، ERP یا وب‌هوک اختصاصی دارید؟ آلتو استودیو آماده پیاده‌سازی ماژول‌های سفارشی است.
                </p>
                <div className="bg-slate-900/80 p-3.5 rounded-xl border border-slate-800 text-sm text-center mb-6 text-slate-300">
                  پشتیبانی پایدار و بدون وقفه
                </div>
              </div>
              <a
                href="https://altostudio.ir"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 glass-panel hover:bg-slate-800 text-white py-3 px-5 rounded-xl font-bold text-sm transition"
              >
                <MessageCircle className="w-4 h-4 text-emerald-400" />
                <span>ارتباط مستقیم با تیم فنی</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SEO article */}
      <section id="about-coresell" className="py-24 relative" aria-labelledby="seo-article-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <h2 id="seo-article-heading" className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              {landingSeo.seoArticle.heading}
            </h2>
            {landingSeo.seoArticle.paragraphs.map((paragraph, index) => (
              <p key={index} className="text-slate-400 text-lg leading-loose mb-6 text-justify">
                {paragraph}
              </p>
            ))}
          </article>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-slate-900/30 border-t border-slate-800" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-bold text-white mb-4">سوالات متداول</h2>
            <p className="text-slate-400">پاسخ پرسش‌های رایج درباره مدیریت موجودی و قیمت‌گذاری چند کاناله با کورسل</p>
          </div>
          <div className="space-y-4">
            {landingSeo.faq.map((item, index) => (
              <details
                key={index}
                className="glass-panel rounded-xl p-5 group"
              >
                <summary className="font-bold text-white cursor-pointer list-none flex justify-between items-center gap-4">
                  {item.question}
                  <span className="text-blue-400 text-xl shrink-0 group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
                </summary>
                <p className="text-slate-400 mt-4 leading-relaxed text-sm md:text-base">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="cta-heading">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 to-brand-950" />
        <div className="absolute right-0 top-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 relative z-10 text-center">
          <h2 id="cta-heading" className="text-4xl md:text-5xl font-bold text-white mb-8">
            آماده‌اید فروش آنلاین خود را بی‌دردسر و هوشمند کنید؟
          </h2>
          <p className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            پیوستن به کورسل یعنی خداحافظی با کارهای دستی، خطای قیمت و استرس موجودی منفی. راه‌اندازی سیستم کمتر از ۵ دقیقه زمان می‌برد.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={goToPanel}
              className="bg-white text-slate-900 px-10 py-4 rounded-full font-bold text-lg hover:bg-slate-100 transition shadow-[0_0_20px_rgba(255,255,255,0.2)] flex items-center justify-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              ایجاد حساب کاربری رایگان
            </button>
            <a
              href="https://altostudio.ir"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-panel text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-slate-800 transition flex items-center justify-center gap-2.5"
            >
              <Globe className="w-5 h-5 text-blue-400" />
              <span>پشتیبانی و تماس در altostudio.ir</span>
            </a>
          </div>
          <p className="mt-6 text-sm text-slate-500">
            <ShieldCheck className="w-4 h-4 inline text-green-500 mb-1" />
            {' '}
            تضمین امنیت کامل اطلاعات، کلیدها و توکن‌های فروشگاهی شما
          </p>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <PackageCheck className="text-blue-500 w-6 h-6" />
              <span className="font-bold text-xl text-white">
                Core<span className="text-blue-400">Sell</span>
              </span>
            </div>
            <nav className="flex flex-wrap justify-center gap-6 text-slate-400 text-sm" aria-label="لینک‌های پاورقی">
              <a href="#features" className="hover:text-white transition">مزایا و امکانات</a>
              <a href="#pricing-sync" className="hover:text-white transition">همگام‌سازی قیمت</a>
              <a href="#how-it-works" className="hover:text-white transition">راهنمای اتصال</a>
              <a href="#faq" className="hover:text-white transition">سوالات متداول</a>
              <a href="#contact" className="hover:text-white transition">تماس با ما</a>
            </nav>
            <div className="text-slate-500 text-sm text-center md:text-left">
              &copy; ۲۰۲۶ تمامی حقوق محفوظ است | توسعه و پشتیبانی توسط{' '}
              <a
                href="https://altostudio.ir"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 font-semibold underline underline-offset-4 transition"
              >
                آلتو استودیو (altostudio.ir)
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
